import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Alert,
  Stack,
  Divider,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import type { QbCacheRow } from "./QbPostedBadge";

/**
 * Build the Windows file:// URL for a posted QB transaction's attachment folder.
 *
 * Mirrors `src/services/qb-desktop/attachment-url.ts:buildLocalFileUrl` (what
 * `convex/qbReceipts.ts` writes into the Memo) — both produce the same
 * canonical string. We rebuild here instead of parsing the QB memo
 * field because:
 *
 *  1. The memo on old rows is corrupted with a legacy HTTPS format
 *     (`| Attachment: https://altelix-squadfinance.4clouders.com/attachments/...`)
 *     left over from a previous version of the response-dispatcher. The
 *     backfill action updates QB itself but this frontend needs to work
 *     immediately for every already-posted row without waiting for the
 *     QBWC sync cycle.
 *  2. The path is a pure function of `qbTxnId`, which is already on the
 *     hydrated `QbCacheRow` the modal receives. No round trip needed.
 *
 * Accountant UX note: this URL only resolves on the accountant's PC where
 * the E: drive is mapped to `\\192.168.2.16\Quickbooks Company`. The client
 * explicitly confirmed she's fine with this — it matches the workflow she
 * already uses inside QB Desktop.
 *
 * Altelix-specific constants (drive letter, base path, company folder) are
 * hard-coded here because the frontend has no access to the backend env
 * vars (`QB_ATTACH_DRIVE` / `QB_COMPANY_NAME`) that parametrize the backend
 * helpers. If this ever multi-tenants, wire them through a Convex query.
 */
const QB_ATTACH_DRIVE = "E:";
const QB_ATTACH_BASE = "Quickbooks Company/Attach";
const QB_COMPANY_NAME = "ALTELIX, LLC";

function buildLocalFolderUrl(qbTxnId: string | undefined): string | null {
  if (!qbTxnId) return null;
  const path = `${QB_ATTACH_DRIVE}/${QB_ATTACH_BASE}/${QB_COMPANY_NAME}/Txn/${qbTxnId}/`;
  return "file:///" + path.replace(/ /g, "%20");
}

type Props = {
  open: boolean;
  onClose: () => void;
  txDate: string;
  qb: QbCacheRow;
};

/**
 * W4 — Teaches the user how to locate a Squad-Finance-posted transaction
 * inside QuickBooks Desktop. Content is conditional on the QB `txnType` so
 * the instructions always match reality (e.g. refunds live in the Credit
 * column, not the Charge column — a repeat pain point for the client).
 */
export function HowToFindModal({ open, onClose, txDate, qb }: Props) {
  const [copied, setCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);

  if (!qb) return null;

  const fileUrl = buildLocalFolderUrl(qb.qbTxnId);

  const copyRef = async () => {
    if (!qb.refNumber) return;
    try {
      await navigator.clipboard.writeText(qb.refNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore clipboard failures (non-secure context, etc.)
    }
  };

  const copyFileUrl = async () => {
    if (!fileUrl) return;
    try {
      await navigator.clipboard.writeText(fileUrl);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 1500);
    } catch {
      // ignore clipboard failures (non-secure context, etc.)
    }
  };

  const headline = (() => {
    switch (qb.txnType) {
      case "CreditCardCharge":
        return (
          <>
            This was posted as a <strong>Credit Card Charge</strong>
            {qb.accountName ? (
              <>
                {" "}
                in account <code>{qb.accountName}</code>.
              </>
            ) : (
              "."
            )}
          </>
        );
      case "CreditCardCredit":
        return (
          <>
            This was posted as a <strong>Credit Card Credit (refund)</strong>
            {qb.accountName ? (
              <>
                {" "}
                in account <code>{qb.accountName}</code>.
              </>
            ) : (
              "."
            )}
          </>
        );
      case "Check":
        return (
          <>
            This was posted as a <strong>Check</strong>
            {qb.accountName ? (
              <>
                {" "}
                in account <code>{qb.accountName}</code>.
              </>
            ) : (
              "."
            )}
          </>
        );
      case "Bill":
        return (
          <>
            This was posted as a <strong>Bill</strong>
            {qb.vendorName ? (
              <>
                {" "}
                to <code>{qb.vendorName}</code>.
              </>
            ) : (
              "."
            )}
          </>
        );
      case "Deposit":
        return (
          <>
            This was posted as a <strong>Deposit</strong>
            {qb.accountName ? (
              <>
                {" "}
                in account <code>{qb.accountName}</code>.
              </>
            ) : (
              "."
            )}
          </>
        );
      case "JournalEntry":
        return (
          <>
            This was posted as a <strong>Journal Entry</strong>.
          </>
        );
      default:
        return (
          <>
            This was posted as <strong>{qb.txnType}</strong>.
          </>
        );
    }
  })();

  const isRefund = qb.txnType === "CreditCardCredit";
  const isCcOrBankish =
    qb.txnType === "CreditCardCharge" ||
    qb.txnType === "CreditCardCredit" ||
    qb.txnType === "Check" ||
    qb.txnType === "Deposit";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Finding this transaction in QuickBooks Desktop
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {headline}
        </Typography>

        {isRefund && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Refunds appear in the <strong>Credit</strong> column, NOT the{" "}
            <strong>Charge</strong> column. If you are searching the Charge
            column you will not find this row.
          </Alert>
        )}

        {/*
          "Open invoice folder" shortcut.
          The memo field on posted CC charges is populated by Agent 3's
          auto-memo pipeline with a `file://E:/Quickbooks Company/Attach/...`
          URL pointing at the folder on the client's accounting PC that
          holds the proof PDFs uploaded by the sync-agent. The client
          asked for a one-click button (previously they had to open the
          QB memo field and copy the URL manually out of the Excel export).

          Caveats:
          - Some browsers (notably Edge/Chrome on HTTPS pages) block
            navigation to `file://` URLs for security. We render a real
            <a href> so the browser actually tries the navigation instead
            of silently failing from a JS window.open call — and we also
            provide a Copy URL button as a robust fallback.
          - No `noopener` / `noreferrer` because those are HTTP concerns
            and break some legacy file:// open-in-explorer behavior.
        */}
        {fileUrl && (
          <Alert
            severity="info"
            icon={<FolderOpenIcon fontSize="small" />}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              Invoice / proof attachments for this charge
            </Typography>
            <Typography
              variant="caption"
              component="div"
              sx={{
                mb: 1.5,
                fontFamily: "monospace",
                wordBreak: "break-all",
                color: "text.secondary",
              }}
            >
              {decodeURIComponent(fileUrl)}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="contained"
                component="a"
                href={fileUrl}
                target="_blank"
                startIcon={<FolderOpenIcon fontSize="small" />}
              >
                Open folder in new tab
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  urlCopied ? (
                    <CheckIcon fontSize="small" />
                  ) : (
                    <ContentCopyIcon fontSize="small" />
                  )
                }
                onClick={copyFileUrl}
              >
                {urlCopied ? "Copied" : "Copy URL"}
              </Button>
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              If the browser blocks the link, use <strong>Copy URL</strong>{" "}
              and paste it into the address bar or Windows Explorer.
            </Typography>
          </Alert>
        )}

        <Divider sx={{ my: 2 }}>Quick reference</Divider>

        <Stack spacing={0.5} sx={{ mb: 2, fontSize: "0.875rem" }}>
          {qb.accountName && (
            <Box>
              <strong>Account:</strong> {qb.accountName}
            </Box>
          )}
          {qb.vendorName && (
            <Box>
              <strong>Vendor:</strong> {qb.vendorName}
            </Box>
          )}
          {qb.txnDate && (
            <Box>
              <strong>QB Date:</strong> {qb.txnDate}
              {qb.txnDate !== txDate && (
                <Typography
                  component="span"
                  color="error"
                  variant="caption"
                  sx={{ ml: 1 }}
                >
                  (bank statement shows {txDate})
                </Typography>
              )}
            </Box>
          )}
          {qb.refNumber && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <strong>Ref #:</strong>
              <code>{qb.refNumber}</code>
              <Tooltip title={copied ? "Copied" : "Copy ref number"}>
                <IconButton size="small" onClick={copyRef}>
                  {copied ? (
                    <CheckIcon fontSize="small" />
                  ) : (
                    <ContentCopyIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          )}
          <Box>
            <strong>QB TxnID:</strong> <code>{qb.qbTxnId}</code>
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }}>Step by step — via Balance Sheet (recommended)</Divider>

        {isCcOrBankish && (
          <Box component="ol" sx={{ pl: 3, m: 0 }}>
            <li>Open QuickBooks Desktop.</li>
            <li>
              Go to <strong>Reports → Company &amp; Financial → Balance
              Sheet Standard</strong>.
            </li>
            <li>
              In the report, locate the{" "}
              <strong>Credit Card</strong> section
              {qb.txnType === "Check" || qb.txnType === "Deposit"
                ? " or the Bank Accounts section (for checks/deposits)"
                : ""}
              .
            </li>
            <li>
              <strong>Double-click</strong> on{" "}
              {qb.accountName ? (
                <code>{qb.accountName}</code>
              ) : (
                "the account row"
              )}{" "}
              to QuickZoom into the <em>Transactions by Account</em> detail
              report.
            </li>
            <li>
              (Optional) Set the date range at the top to cover{" "}
              <code>{qb.txnDate ?? txDate}</code> so the list is short.
            </li>
            {qb.refNumber ? (
              <li>
                Press <kbd>Ctrl</kbd>+<kbd>F</kbd> and paste the reference
                number <code>{qb.refNumber}</code> — it appears in the{" "}
                <em>Num</em> column.
              </li>
            ) : (
              <li>
                Scan the list by date ({qb.txnDate ?? txDate}) and amount.
              </li>
            )}
            {isRefund && (
              <li>
                The row appears with <strong>Type = Credit Card Credit</strong>{" "}
                (not <em>Credit Card Charge</em>) — check the <em>Type</em>{" "}
                column if you are unsure.
              </li>
            )}
            <li>
              Double-click the matching row to open the full transaction.
            </li>
          </Box>
        )}

        {qb.txnType === "Bill" && (
          <Box component="ol" sx={{ pl: 3, m: 0 }}>
            <li>Open QuickBooks Desktop.</li>
            <li>
              Go to <strong>Reports → Company &amp; Financial → Balance
              Sheet Standard</strong>.
            </li>
            <li>
              Locate <strong>Accounts Payable</strong> in the Liabilities
              section and double-click to QuickZoom into the detail report.
            </li>
            {qb.vendorName && (
              <li>
                Filter / search by vendor <code>{qb.vendorName}</code>.
              </li>
            )}
            {qb.refNumber && (
              <li>
                Find the row with Ref # <code>{qb.refNumber}</code>.
              </li>
            )}
          </Box>
        )}

        {qb.txnType === "JournalEntry" && (
          <Box component="ol" sx={{ pl: 3, m: 0 }}>
            <li>Open QuickBooks Desktop.</li>
            <li>
              Go to <strong>Company → Make General Journal Entries</strong>.
            </li>
            <li>
              Use <strong>Previous / Find</strong> or press{" "}
              <kbd>Ctrl</kbd>+<kbd>F</kbd> and search by date / ref.
            </li>
          </Box>
        )}

        <Divider sx={{ my: 2 }}>Alternative — direct register</Divider>

        {isCcOrBankish && (
          <Typography variant="body2">
            You can also open the register directly:{" "}
            <strong>Banking → Use Register</strong>
            {qb.accountName && (
              <>
                {" "}→ select <code>{qb.accountName}</code>
              </>
            )}{" "}
            → press <kbd>Ctrl</kbd>+<kbd>F</kbd> and paste{" "}
            {qb.refNumber ? (
              <>
                <code>{qb.refNumber}</code> in the <em>Number/Ref</em> field
              </>
            ) : (
              <>the date / amount</>
            )}
            .
          </Typography>
        )}

        {!isCcOrBankish && (
          <Typography variant="body2">
            Use <strong>Edit → Find → Advanced</strong> and filter by Date
            range, Amount, and transaction type.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
